import { useRotationStore } from '../context/RotationContext';
import { Rotation } from '../types/ViewModel/Rotation';
import { PortCall } from '../types/ViewModel/PortCall';
import { BerthCall } from '../types/ViewModel/BerthCall';
import { CargoOperation } from '../types/ViewModel/CargoOperation';
import { RotationTreeEnum } from '../enums/rotationTreeEnum';
import { InputDataTypeEnum } from '../enums/inputDataTypeEnum';
import { Port } from '../types/ViewModel/Port';
import { TankersEvent } from '../types/ViewModel/TankersEvent';
import { MoveEventsPortCall } from '../types/ViewModel/MoveEventsPortCall';
import { MoveEventsCargoOperation } from '../types/ViewModel/MoveEventsCargoOperation';
import { DropdownTypeEnum } from '../enums/dropdownTypeEnum';
import { IRotationNode } from '../types/ViewModel/IRotationNode';
import { MoveEventsPortCallFactory } from '../factory/MoveEventsPortCallFactory';
import { MoveEventsCargoOperationFactory } from '../factory/MoveEventsCargoOperationFactory';
import { MoveEventsBerthCallFactory } from '../factory/MoveEventsBerthCallFactory';
import { useDateStore } from '../context/DateContext';
import { useVesselStore } from '../context/VesselContext';
import { ReportLocationEnum } from '../enums/reportLocationEnum';
import { prettifyDateTime } from 'odfjell-date-time-utils/dateTimeFormatter';
import { isDateAfter, addDateDays, isDateBefore, getUtcTimeNow } from 'odfjell-date-time-utils/dateTimeUtils';
import { newPortFromCode } from 'src/factory/ReportEvents/Rotation/PortFactory';
import { newCargoOperationFromCode, newDeletedCargoOperationFromCode } from 'src/factory/ReportEvents/Rotation/CargoOperationFactory';
import { newDeletedBerthCallFromCode } from 'src/factory/ReportEvents/Rotation/BerthCallFactory';
import { newDeletedPortCallFromCode } from 'src/factory/ReportEvents/Rotation/PortCallFactory';

export default () => {
    const moveEventsPortCallFactory = new MoveEventsPortCallFactory();
    const moveEventsBerthCallFactory = new MoveEventsBerthCallFactory();
    const moveEventsCargoOperationFactory = new MoveEventsCargoOperationFactory();
    const { updatedRotation, setNextPortCall, unlockedBerthCalls } = useRotationStore();
    const { portInPortClusterList } = useRotationStore();
    const { selectedVessel } = useVesselStore();

    const { portCash } = useDateStore();

    const rotationAndImosSyncNotUpToDateMsg = (rotation: Rotation): string | null => {
        if (isDateAfter(rotation.lastImosUpdatedUtc, rotation.lastImosSynchronizedUtc)) {
            const syncDatePlussAddedDays = addDateDays(rotation.lastImosSynchronizedUtc, 3);
            if (isDateBefore(syncDatePlussAddedDays, getUtcTimeNow())) {
                return 'Master stowage plan not synchronized';
            }
        }
        return null;
    };

    const rotationAndImosSyncNotUpToDateTooltip = (rotation: Rotation): string | null => {
        if (isDateAfter(rotation.lastImosUpdatedUtc, rotation.lastImosSynchronizedUtc)) {
            const syncDatePlussAddedDays = addDateDays(rotation.lastImosSynchronizedUtc, 3);
            if (isDateBefore(syncDatePlussAddedDays, getUtcTimeNow())) {
                return (
                    'IMOS was updated ' +
                    prettifyDateTime(rotation.lastImosUpdatedUtc) +
                    '. This is after the last ORCA synchronization with IMOS (' +
                    prettifyDateTime(rotation.lastImosSynchronizedUtc) +
                    ') and more than 3 days ago. Please synchronize with IMOS.'
                );
            }
        }
        return null;
    };

    const resetMovedFromClickedFlag = (rotation: Rotation, node: IRotationNode | null) => {
        rotation.portCallArray.forEach((o) => (o.movedFromClicked = false));
        const bcList = rotation.portCallArray.flatMap((o) => o.berthCallArray);
        bcList.forEach((o) => (o.movedFromClicked = false));
        const coList = bcList.flatMap((o) => o.cargoOperationArray);
        coList.forEach((o) => (o.movedFromClicked = false));
        if (node && node.actionMsg.id) {
            // Set move-to rotation node color
            switch (node.type) {
                case RotationTreeEnum.CargoOperation: {
                    const bc1 = bcList.find((o) => o.berthCallId === node.actionMsg.id);
                    if (bc1) {
                        const co = bc1.cargoOperationArray.find((o) => o.id === node.id);
                        if (co) {
                            co.movedFromClicked = true;
                        }
                    }
                    break;
                }
                case RotationTreeEnum.BerthCall: {
                    const bc2 = bcList.find((o) => o.id === node.actionMsg.id);
                    if (bc2) {
                        bc2.movedFromClicked = true;
                    }
                    break;
                }
                case RotationTreeEnum.PortCall: {
                    const pc = rotation.portCallArray.find((o) => o.id === node.actionMsg.id);
                    if (pc) {
                        pc.movedFromClicked = true;
                    }
                    break;
                }
                case RotationTreeEnum.Sea:
                    break;
            }
        }
    };

    const getParentBerthCallFromNode = (rotationNode: IRotationNode): BerthCall | null => {
        const cargo: CargoOperation = rotationNode as CargoOperation;
        return cargo.berthCall;
    };

    const getParentPortCallFromNode = (rotationNode: IRotationNode | null): PortCall | null => {
        const bc: BerthCall = rotationNode as BerthCall;
        return bc.portCall;
    };

    const getPortCall = (rot: Rotation | null, id: number, reportLocation: ReportLocationEnum): PortCall | null => {
        if (rot) {
            const pc = rot.portCallArray.find((o) => o.portCallId === id && o.reportLocation === reportLocation);
            return pc ?? null;
        }
        return null;
    };

    const getPreviousPortCall = (id: number | null): PortCall | null => {
        if (id) {
            const pcIndex = updatedRotation?.portCallArray.findIndex((pc) => pc.portCallId === id);
            if (pcIndex && pcIndex > 0) {
                const pc = updatedRotation?.portCallArray[pcIndex - 1];
                return pc ?? null;
            }
        }
        return null;
    };

    const getPortCallIndex = (portCallId: number | null): number => {
        if (portCallId && updatedRotation) {
            return updatedRotation.portCallArray.findIndex((pc) => pc.portCallId === portCallId);
        }
        return -1;
    };

    const getBerthCallIndex = (portCallId: number, berthCallId: number | null): number => {
        if (berthCallId && updatedRotation) {
            const pc = updatedRotation.portCallArray.find((o) => o.portCallId === portCallId);
            if (pc) {
                return pc.berthCallArray.findIndex((bc) => bc.berthCallId === berthCallId);
            }
        }
        return -1;
    };

    const getCargoOperationIndex = (portCallId: number, berthCallId: number, cargoOpid: number | null): number => {
        if (cargoOpid && updatedRotation) {
            const pc = updatedRotation.portCallArray.find((o) => o.portCallId === portCallId);
            if (pc) {
                const bc = pc.berthCallArray.find((bc) => bc.berthCallId === berthCallId);
                if (bc) {
                    return bc.cargoOperationArray.findIndex((co) => co.cargoOperationId === cargoOpid);
                }
            }
        }
        return -1;
    };

    const getBerthCall = (rot: Rotation | null, portCallId: number, berthCallId: number): BerthCall | null => {
        if (rot) {
            const portCall = rot.portCallArray.find((pc) => pc.portCallId === portCallId);
            if (portCall) {
                const bc = portCall.berthCallArray.find((bc) => bc.berthCallId === berthCallId);
                return bc ?? null;
            }
        }
        return null;
    };

    const getBerthCallsForPortCall = (rotation: Rotation, portCallId: number): BerthCall[] => {
        const berthCallArray = rotation?.portCallArray.flatMap((o) => o.berthCallArray);
        const bcList = berthCallArray?.filter((bc) => bc.portCall && bc.portCall.portCallId === portCallId);
        return bcList ?? [];
    };

    const getCargoOperation = (rot: Rotation | null, portCallId: number, berthCallId: number, cargoOpId: number): CargoOperation | null => {
        if (rot) {
            const portCall = rot.portCallArray.find((pc) => pc.portCallId === portCallId);
            if (portCall) {
                const berthCall = portCall.berthCallArray.find((bc) => bc.berthCallId === berthCallId);
                if (berthCall) {
                    const co = berthCall.cargoOperationArray.find((co) => co.cargoOperationId === cargoOpId);
                    return co ?? null;
                }
            }
        }
        return null;
    };

    const getCargoOperationFromId = (rotation: Rotation | null, cargoOpId: number): CargoOperation | null => {
        if (rotation) {
            const fullCargoOpArray = rotation.portCallArray.flatMap((o) => o.berthCallArray.flatMap((p) => p.cargoOperationArray));
            const cargoOpList = fullCargoOpArray.filter((co) => co.cargoOperationId === cargoOpId);
            if (cargoOpList && cargoOpList.length > 0) {
                if (cargoOpList.length === 1) return cargoOpList[0];
                const cargoOpListNotMovedDeleted = fullCargoOpArray.filter((co) => !co.isDeleted && !co.isMoved);
                if (cargoOpListNotMovedDeleted && cargoOpListNotMovedDeleted.length > 0) return cargoOpListNotMovedDeleted[0];
                const cargoOpListMovedOrDeleted = fullCargoOpArray.filter((co) => co.isDeleted || co.isMoved);
                if (cargoOpListMovedOrDeleted && cargoOpListMovedOrDeleted.length > 0) return cargoOpListMovedOrDeleted[0];
            }
        }
        return null;
    };

    const getPortCallFromNode = (node: IRotationNode | null): PortCall | null => {
        if (node) {
            switch (node?.type) {
                case RotationTreeEnum.Sea:
                    return node as PortCall;
                case RotationTreeEnum.PortCall:
                    return node as PortCall;
                case RotationTreeEnum.BerthCall:
                    return (node as BerthCall).portCall;
                case RotationTreeEnum.CargoOperation: {
                    const bc = (node as CargoOperation).berthCall;
                    if (bc) {
                        return bc.portCall;
                    }
                    break;
                }
                default:
                    break;
            }
        }
        return null;
    };

    const getBerthCallFromNode = (node: IRotationNode | null): BerthCall | null => {
        if (node) {
            switch (node?.type) {
                case RotationTreeEnum.BerthCall:
                    return node as BerthCall;
                case RotationTreeEnum.CargoOperation:
                    return (node as CargoOperation).berthCall;
            }
        }
        return null;
    };

    const getCargoOperationFromNode = (node: IRotationNode | null): CargoOperation | null => {
        if (node) {
            switch (node?.type) {
                case RotationTreeEnum.CargoOperation:
                    return node as CargoOperation;
            }
        }
        return null;
    };

    const getRequiredContextObj = (node: IRotationNode | null, requiredLevel: RotationTreeEnum): IRotationNode | null => {
        if (node) {
            switch (node.type) {
                case RotationTreeEnum.Sea:
                    return node;
                case RotationTreeEnum.PortCall:
                    return node;
                case RotationTreeEnum.BerthCall:
                    if (requiredLevel === node.type) {
                        return node;
                    } else {
                        return getParentPortCallFromNode(node);
                    }
                case RotationTreeEnum.CargoOperation:
                    switch (requiredLevel) {
                        case RotationTreeEnum.PortCall: {
                            const berthCall = getParentBerthCallFromNode(node);
                            if (berthCall) {
                                return getParentPortCallFromNode(berthCall);
                            }
                            break;
                        }
                        case RotationTreeEnum.BerthCall:
                            return getParentBerthCallFromNode(node);
                        case RotationTreeEnum.CargoOperation:
                            return node;
                    }
                    break;
            }
        }
        return null;
    };

    const getPortCallIdFromNode = (node: IRotationNode): number | null => {
        switch (node.type) {
            case RotationTreeEnum.Sea:
                return node.id;
            case RotationTreeEnum.PortCall:
                return node.id;
            case RotationTreeEnum.BerthCall: {
                const portCall = getParentPortCallFromNode(node);
                if (portCall) {
                    return portCall.portCallId;
                }
                break;
            }
            case RotationTreeEnum.CargoOperation: {
                const berthCall = getParentBerthCallFromNode(node);
                if (berthCall) {
                    const portCall2 = getParentPortCallFromNode(berthCall);
                    if (portCall2) {
                        return portCall2.portCallId;
                    }
                }
                break;
            }
        }
        return null;
    };

    const getParentPortForMovedToBerthCall = (rotation: Rotation, berthCallId: number | null): PortCall | null => {
        if (berthCallId) {
            const berthCallArray = rotation.portCallArray.flatMap((o) => o.berthCallArray);
            const berthCall = berthCallArray?.find((bc) => bc.berthCallId === berthCallId && !bc.isMoved);
            if (berthCall) {
                return berthCall.portCall;
            }
        }
        return null;
    };

    const getParentBerthForMovedToCargoOp = (rotation: Rotation, cargoOperationId: number | null): BerthCall | null => {
        if (cargoOperationId) {
            const berthCallArray = rotation.portCallArray.flatMap((o) => o.berthCallArray);
            const cargoOperationArray = berthCallArray?.flatMap((o) => o.cargoOperationArray);
            const cargo = cargoOperationArray?.find((c) => c.cargoOperationId === cargoOperationId && !c.isMoved);
            if (cargo) {
                return cargo.berthCall;
            }
        }
        return null;
    };

    const getParentBerthForMovedToCargoOpSamePortCall = (cargoOperation: CargoOperation | null): BerthCall | null => {
        if (cargoOperation) {
            const parentBc = getParentBerthCallFromNode(cargoOperation);
            if (parentBc) {
                const parentPc = getParentPortCallFromNode(parentBc);
                if (parentPc) {
                    const cargoOperationArray = parentPc.berthCallArray?.flatMap((o) => o.cargoOperationArray);
                    const cargo = cargoOperationArray?.find((c) => c.cargoOperationId === cargoOperation.cargoOperationId && !c.isMoved);
                    if (cargo) {
                        return cargo.berthCall;
                    }
                }
            }
        }
        return null;
    };

    const getParentBerthForMovedFromCargoOp = (cargoOperation: CargoOperation | null): BerthCall | null => {
        if (cargoOperation) {
            const parentBc = getParentBerthCallFromNode(cargoOperation);
            if (parentBc && cargoOperation.isMoved) {
                return parentBc;
            }
        }
        return null;
    };

    const filterCargoOperation = (
        dropdownTypeId: DropdownTypeEnum,
        cargoOperation: CargoOperation,
        includeDeleted: boolean,
        includeMoved: boolean
    ): boolean => {
        if (!includeDeleted && cargoOperation.isDeleted) {
            return false;
        }
        if (!includeMoved && cargoOperation.isMoved) {
            return false;
        }

        switch (dropdownTypeId) {
            case DropdownTypeEnum.CargoLoadingAndUnloadingOperations:
                return cargoOperation.isLoading || cargoOperation.isUnloading;
            case DropdownTypeEnum.CargoLoadingOperations:
                return cargoOperation.isLoading;
            case DropdownTypeEnum.CargoUnloadingOperations:
                return cargoOperation.isUnloading;
        }
        return false;
    };

    const getBerthName = (rotation: Rotation, berthId: number | null): string | null => {
        const bName: string | null = null;
        if (berthId) {
            const berthCallArray = rotation.portCallArray.flatMap((o) => o.berthCallArray);
            if (berthCallArray) {
                const bc = berthCallArray.find((o) => o.berthId === berthId);
                if (bc && bc.name) {
                    return bc.name;
                }
            }
        }
        return bName;
    };

    const getBerthNameWithoutClusterName = (rotation: Rotation, berthId: number | null): string | null => {
        const bName: string | null = null;
        if (berthId) {
            const berthCallArray = rotation.portCallArray.flatMap((o) => o.berthCallArray);
            if (berthCallArray) {
                const bc = berthCallArray.find((o) => o.berthId === berthId);
                if (bc && bc.name) {
                    return bc.name;
                }
            }
        }
        return bName;
    };

    const unlockedByUser = (berthCallId: number, unlock: boolean) => {
        const alreadyUnlockedIndex = unlockedBerthCalls.findIndex((bc) => bc === berthCallId);
        if (alreadyUnlockedIndex > -1) {
            if (!unlock) {
                unlockedBerthCalls.splice(alreadyUnlockedIndex, 1);
            }
        } else {
            if (unlock) {
                unlockedBerthCalls.push(berthCallId);
            }
        }
    };

    const setBerthUnlockedByUser = (berthCallId: number, unlock: boolean) => {
        unlockedByUser(berthCallId, unlock);
    };

    const getBerthUnlockedByUser = (berthCallId: number) => {
        const alreadyUnlocked = unlockedBerthCalls.find((bc) => bc === berthCallId);
        return !!alreadyUnlocked;
    };

    const findEventsWithoutRotationNode = (rotation: Rotation, fullEventList: TankersEvent[]): TankersEvent[] => {
        const evList: TankersEvent[] = [];
        const portCallHash: any = getPortCallHash(rotation);
        fullEventList.map((e: TankersEvent) => {
            if (!e.deleted) {
                if (e.cargoOperationId && e.portCallId) {
                    const portCall: PortCall = portCallHash[e.portCallId];
                    if (!portCall) {
                        evList.push(e);
                    } else {
                        const berthCall = portCall.berthCallArray.find((bc: BerthCall) => bc.berthCallId === e.berthCallId);
                        if (!berthCall) {
                            evList.push(e);
                        } else {
                            if (!berthCall.cargoOperationArray.find((co: CargoOperation) => co.cargoOperationId === e.cargoOperationId)) {
                                evList.push(e);
                            }
                        }
                    }
                } else if (e.berthCallId && e.portCallId) {
                    const portCall: PortCall = portCallHash[e.portCallId];
                    if (!portCall) {
                        evList.push(e);
                    } else {
                        if (!portCall.berthCallArray.find((bc: BerthCall) => bc.berthCallId === e.berthCallId)) {
                            evList.push(e);
                        }
                    }
                } else if (e.portCallId) {
                    if (!portCallHash[e.portCallId]) {
                        evList.push(e);
                    }
                }
            }
        });
        return evList;
    };

    const getPortRotationNode = (rotation: Rotation, portCallId: number | null, eventTypeContextId: ReportLocationEnum | null): PortCall | null => {
        if (portCallId && eventTypeContextId) {
            // First try exact match on both portCallId AND reportLocation
            let pc = rotation?.portCallArray.find((pc) => pc.portCallId === portCallId && pc.reportLocation === eventTypeContextId);
            if (pc) return pc;

            // If no exact match, fallback to just portCallId match (handles cases where port call exists with different reportLocation)
            pc = rotation?.portCallArray.find((pc) => pc.portCallId === portCallId);
            return pc ?? null;
        }
        return null;
    };

    const hasSameVoyageNumber = (sourceVoyageNumber: string, targetVoyageNumber: string): boolean => {
        if (sourceVoyageNumber && targetVoyageNumber) {
            if (sourceVoyageNumber === targetVoyageNumber) {
                return true;
            }
            if (sourceVoyageNumber.includes(targetVoyageNumber)) {
                return true;
            }
            if (targetVoyageNumber.includes(sourceVoyageNumber)) {
                return true;
            }
        }
        return false;
    };

    const getPortCallsFromPortIdWithoutEvents = (portCallArray: PortCall[], sourcePortCall: PortCall, eventList: TankersEvent[]): PortCall[] => {
        const pcList: PortCall[] = [];
        portCallArray.forEach((pc) => {
            if (!eventList.find((ev: TankersEvent) => !ev.deleted && ev.portCallId === pc.portCallId)) {
                // Check if same port id
                // Check for port cluster
                if (pc.isInPortCluster && sourcePortCall.portClusterInitialPortId) {
                    if (pc.port && pc.port.portId === sourcePortCall.portClusterInitialPortId && pc.portCallId !== sourcePortCall.portCallId) {
                        pcList.push(pc);
                    }
                } else {
                    if (pc.port && pc.port.portId === sourcePortCall?.port?.portId && pc.portCallId !== sourcePortCall.portCallId) {
                        pcList.push(pc);
                    }
                }
            }
        });
        return pcList;
    };

    const getPortCallHash = (rotation: Rotation): any => {
        const portCallHash: any = {};
        rotation.portCallArray.forEach((pc: PortCall) => {
            // Include both Port AND Sea port calls in the hash
            if (pc.reportLocation === ReportLocationEnum.Port || pc.reportLocation === ReportLocationEnum.Sea) {
                portCallHash[pc.portCallId] = pc;
            }
        });
        return portCallHash;
    };

    const getBerthRotationNode = (rotation: Rotation, portCallId: number | null, berthCallId: number | null): BerthCall | null => {
        const portCall = rotation.portCallArray.find((pc) => pc.portCallId === portCallId);
        if (portCall) {
            const bc = portCall.berthCallArray.find((bc) => bc.berthCallId === berthCallId);
            return bc ?? null;
        }
        return null;
    };

    const getCargoOpRotationNode = (
        rotation: Rotation | null,
        portCallId: number | null,
        berthCallId: number | null,
        cargoOperationId: number | null
    ): CargoOperation | null => {
        if (rotation) {
            const portCall = rotation.portCallArray.find((pc) => pc.portCallId === portCallId);
            if (portCall) {
                const berthCall = portCall.berthCallArray.find((bc) => bc.berthCallId === berthCallId);
                if (berthCall) {
                    return berthCall.cargoOperationArray.find((co) => co.cargoOperationId === cargoOperationId) ?? null;
                }
            }
        }
        return null;
    };

    const getMovedCargoOpRotationNode = (
        rotation: Rotation,
        portCallId: number | null,
        berthCallId: number | null,
        cargoOperationId: number | null
    ): CargoOperation | null => {
        let movedCargoOp: CargoOperation | null = null;
        if (rotation.portCallArray && rotation.portCallArray.length > 0) {
            let keepGoingPc = true;
            let pcInd = 0;
            while (keepGoingPc) {
                if (rotation.portCallArray[pcInd].berthCallArray && rotation.portCallArray[pcInd].berthCallArray.length > 0) {
                    let keepGoingBc = true;
                    let bcInd = 0;
                    while (keepGoingBc) {
                        if (
                            rotation.portCallArray[pcInd].portCallId !== portCallId ||
                            rotation.portCallArray[pcInd].berthCallArray[bcInd].berthCallId !== berthCallId
                        ) {
                            const cargoOp = rotation.portCallArray[pcInd].berthCallArray[bcInd].cargoOperationArray.find(
                                (co) => co.cargoOperationId === cargoOperationId
                            );
                            if (cargoOp) {
                                movedCargoOp = cargoOp;
                                keepGoingBc = false;
                            } else {
                                bcInd++;
                                if (bcInd === rotation.portCallArray[pcInd].berthCallArray.length) {
                                    keepGoingBc = false;
                                }
                            }
                        } else {
                            bcInd++;
                            if (bcInd === rotation.portCallArray[pcInd].berthCallArray.length) {
                                keepGoingBc = false;
                            }
                        }
                    }
                    if (movedCargoOp) {
                        keepGoingPc = false;
                    } else {
                        pcInd++;
                        if (pcInd === rotation.portCallArray.length) {
                            keepGoingPc = false;
                        }
                    }
                } else {
                    // No berth calls for port call
                    pcInd++;
                    if (pcInd === rotation.portCallArray.length) {
                        keepGoingPc = false;
                    }
                }
            }
        }
        return movedCargoOp;
    };

    const getMovedBerthCallRotationNode = (rotation: Rotation | null, portCallId: number | null, berthCallId: number | null): BerthCall | null => {
        let movedBerthCall: BerthCall | null = null; // TODO: LOGIC-ERROR - take into account berth calls moved to another port call
        if (rotation && rotation.portCallArray && rotation.portCallArray.length > 0) {
            let keepGoingPc = true;
            let pcInd = 0;
            while (keepGoingPc) {
                if (rotation.portCallArray[pcInd].berthCallArray && rotation.portCallArray[pcInd].berthCallArray.length > 0) {
                    if (rotation.portCallArray[pcInd].portCallId !== portCallId) {
                        const berthCall = rotation.portCallArray[pcInd].berthCallArray.find((bc: BerthCall) => bc.berthCallId === berthCallId);
                        if (berthCall) {
                            movedBerthCall = berthCall;
                            keepGoingPc = false;
                        } else {
                            pcInd++;
                            if (pcInd === rotation.portCallArray.length) {
                                keepGoingPc = false;
                            }
                        }
                    } else {
                        pcInd++;
                        if (pcInd === rotation.portCallArray.length) {
                            keepGoingPc = false;
                        }
                    }
                } else {
                    // No berth calls for port call
                    pcInd++;
                    if (pcInd === rotation.portCallArray.length) {
                        keepGoingPc = false;
                    }
                }
            }
        }
        return movedBerthCall;
    };

    const setEventInputRegInvalidForDeletedCargoOp = (fullEventList: TankersEvent[], cargoOperationId: number | null, portCallId: number | null) => {
        const portCallEvents = fullEventList.filter((e) => {
            return e.portCallId === portCallId && !e.deleted;
        });
        portCallEvents.forEach((ev) => {
            if (ev.eventTypeInputRegList && ev.eventTypeInputRegList.length > 0) {
                ev.eventTypeInputRegList.forEach((evInp) => {
                    if (evInp.inputDataTypeId === InputDataTypeEnum.Dropdown) {
                        if (
                            evInp.dropdownGroupReg &&
                            evInp.dropdownGroupReg.dropdownItemRegList &&
                            evInp.dropdownGroupReg.dropdownItemRegList.length > 0
                        ) {
                            evInp.dropdownGroupReg.dropdownItemRegList.forEach((ddItem) => {
                                if (ddItem.dropdownItemId === cargoOperationId) {
                                    evInp.isEmpty = false;
                                    ddItem.isValid = false;
                                }
                            });
                        }
                    }
                });
            }
        });
    };

    const setEventInputRegInvalidForAllMovedCargoOps = (
        fullEventList: TankersEvent[],
        movedFromBerthCallId: number | null,
        cargoOperationId: number | null,
        portCallId: number | null
    ) => {
        const berthCallEvents = fullEventList.filter((e) => {
            return e.portCallId === portCallId && e.berthCallId && e.berthCallId === movedFromBerthCallId && !e.deleted;
        });
        berthCallEvents.forEach((ev) => {
            if (ev.eventTypeInputRegList && ev.eventTypeInputRegList.length > 0) {
                ev.eventTypeInputRegList.forEach((evInp) => {
                    if (evInp.inputDataTypeId === InputDataTypeEnum.Dropdown) {
                        if (
                            evInp.dropdownGroupReg &&
                            evInp.dropdownGroupReg.dropdownItemRegList &&
                            evInp.dropdownGroupReg.dropdownItemRegList.length > 0
                        ) {
                            evInp.dropdownGroupReg.dropdownItemRegList.forEach((ddItem) => {
                                if (ddItem.dropdownItemId === cargoOperationId) {
                                    evInp.isEmpty = false;
                                    ddItem.isValid = false;
                                }
                            });
                        }
                    }
                });
            }
        });
    };

    const createDeletedCargoOperation = (
        rotation: Rotation,
        fullEventList: TankersEvent[],
        ev: TankersEvent,
        cargoOpRotationNode: CargoOperation | null,
        berthCall: BerthCall | null
    ) => {
        if (berthCall) {
            if (cargoOpRotationNode) {
                const cargoOp = getMovedCargoOpRotationNode(rotation, ev.portCallId, ev.berthCallId, ev.cargoOperationId);
                if (cargoOp) {
                    if (cargoOpRotationNode.isMoved) {
                        setEventInputRegInvalidForAllMovedCargoOps(fullEventList, ev.berthCallId, ev.cargoOperationId, ev.portCallId);
                    }
                } else {
                    if (cargoOpRotationNode.isDeleted) {
                        ev.cargoOperationId = cargoOpRotationNode.cargoOperationId;
                        setEventInputRegInvalidForAllMovedCargoOps(fullEventList, ev.berthCallId, ev.cargoOperationId, ev.portCallId);
                    }
                }
            } else {
                const cargoOp = getMovedCargoOpRotationNode(rotation, ev.portCallId, ev.berthCallId, ev.cargoOperationId);
                const movedCargoOp = newCargoOperationFromCode(cargoOp, berthCall);
                if (movedCargoOp) {
                    if (!movedCargoOp.portCall) {
                        movedCargoOp.portCall = berthCall.portCall;
                    }
                    movedCargoOp.isMoved = true;
                    berthCall.cargoOperationArray.push(movedCargoOp);
                    setEventInputRegInvalidForAllMovedCargoOps(fullEventList, ev.berthCallId, ev.cargoOperationId, ev.portCallId);
                } else {
                    const deletedCargoOp = newDeletedCargoOperationFromCode(
                        ev.cargoOperationId,
                        berthCall,
                        ev.cargoNumber,
                        ev.cargoId,
                        ev.imosCargoId,
                        ev.operationType,
                        ev.weight,
                        ev.tradeName,
                        false
                    );
                    // deletedCargoOp.portCall = getParentPortRotationNode(rotation, berthCall.berthCallId);
                    deletedCargoOp.portCall = berthCall.portCall;
                    deletedCargoOp.isDeleted = true;
                    berthCall.cargoOperationArray.push(deletedCargoOp);
                    // Update events with new cargo operation
                    ev.cargoOperationId = deletedCargoOp.cargoOperationId;
                    setEventInputRegInvalidForDeletedCargoOp(fullEventList, ev.cargoOperationId, ev.portCallId);
                }
            }
        }
    };

    const createDeletedBerthCall = (rotation: Rotation, fullEventList: TankersEvent[], ev: TankersEvent, portCall: PortCall): BerthCall | null => {
        let newBerthCall: BerthCall | null = null;
        const berthCallExistInRotation = getBerthRotationNode(rotation, ev.portCallId, ev.berthCallId);
        if (!berthCallExistInRotation) {
            let name: string | null = null;
            let berthName: string | null = null;
            if (ev.portClusterInitialPortName) {
                const bName = getBerthNameWithoutClusterName(rotation, ev.berthId);
                berthName = bName;
                name = ev.portClusterInitialPortName + ' - ' + bName;
            } else {
                name = getBerthName(rotation, ev.berthId);
                berthName = name;
            }
            newBerthCall = newDeletedBerthCallFromCode(ev.berthCallId, ev.berthId, name, berthName, portCall, ev.voyageNumber, ev.berthName);
            if (ev.cargoOperationId) {
                const existingCargoOp = getCargoOpRotationNode(rotation, ev.portCallId, ev.berthCallId, ev.cargoOperationId);
                createDeletedCargoOperation(rotation, fullEventList, ev, existingCargoOp, newBerthCall);
            }
            const movedBerthCall = getMovedBerthCallRotationNode(rotation, ev.portCallId, ev.berthCallId);
            if (movedBerthCall) {
                newBerthCall.isMoved = true;
                newBerthCall.isDeleted = false;
            } else {
                newBerthCall.isMoved = false;
                newBerthCall.isDeleted = true;
            }
            newBerthCall.addedDeletedBerthCall = true;
        } else if (ev.cargoOperationId) {
            const existingCargoOp = getCargoOpRotationNode(rotation, ev.portCallId, ev.berthCallId, ev.cargoOperationId);
            createDeletedCargoOperation(rotation, fullEventList, ev, existingCargoOp, berthCallExistInRotation);
            if (existingCargoOp) {
                const cargoOpAlreadyAdded = berthCallExistInRotation.cargoOperationArray.find((co) => {
                    return co.cargoOperationId === ev.cargoOperationId;
                });
                if (!cargoOpAlreadyAdded) {
                    berthCallExistInRotation.cargoOperationArray.push();
                }
            }
        }
        return newBerthCall;
    };

    const isCargoTheSame = (source: CargoOperation, target: CargoOperation): boolean => {
        if (source.cargoOperationType === target.cargoOperationType && source.displayWeight === target.displayWeight) {
            if (source.imosCargoId && target.imosCargoId) {
                return source.imosCargoId === target.imosCargoId;
            }
            return source.cargoId === target.cargoId;
        }
        return false;
    };

    const getTradeName = (rotation: Rotation, cargoNumber: string): string => {
        const cargoOperations = rotation.portCallArray.flatMap((o) => o.berthCallArray.flatMap((p) => p.cargoOperationArray));
        return cargoOperations.find((o) => o.cargoNumber === cargoNumber)?.tradeName ?? '???';
    };

    const berthCallsAndCargoOpsExist = (sourcePortCall: PortCall, targetPortCall: PortCall): MoveEventsPortCall | null => {
        let moveEventsPortCall: MoveEventsPortCall | null = moveEventsPortCallFactory.createMoveEventsPortCallItem(
            0,
            targetPortCall.voyageNumberPresentationString,
            sourcePortCall.portCallId,
            targetPortCall.portCallId,
            targetPortCall.port?.portId ?? null,
            []
        );
        if (!sourcePortCall.berthCallArray || sourcePortCall.berthCallArray.length === 0) {
            return null;
        }
        if (!targetPortCall.berthCallArray || targetPortCall.berthCallArray.length === 0) {
            // No berth call for target port call
            return null;
        }
        let bcCont = true;
        let bcIndex = 0;
        // For each berth call in sourcePortCall
        while (bcCont) {
            const bcTargetList = targetPortCall.berthCallArray.filter((bc) => bc.berthId === sourcePortCall.berthCallArray[bcIndex].berthId);
            if (bcTargetList && bcTargetList.length > 0) {
                // One or more berth calls with the same berthId
                let bcTargetCont = true;
                let bcTargetIndex = 0;
                let bcTargetExist = true;
                const moveEventsBc = moveEventsBerthCallFactory.createMoveEventsBerthCallItem(
                    sourcePortCall.berthCallArray[bcIndex].berthCallId,
                    bcTargetList[bcTargetIndex].berthCallId,
                    []
                );
                if (
                    sourcePortCall.berthCallArray[bcIndex].cargoOperationArray &&
                    sourcePortCall.berthCallArray[bcIndex].cargoOperationArray.length > 0
                ) {
                    // Has cargo operations for source berth call
                    while (bcTargetCont) {
                        // For each berth call
                        bcTargetExist = false;
                        if (bcTargetList[bcTargetIndex].cargoOperationArray && bcTargetList[bcTargetIndex].cargoOperationArray.length > 0) {
                            // Has cargo operations for target berth call
                            let coExist = true;
                            let coCont = true;
                            let coIndex = 0;
                            const moveEventsCoList: MoveEventsCargoOperation[] = [];
                            // Check that each source cargo operation exist in the target berth call
                            while (coCont) {
                                const targetCo = bcTargetList[bcTargetIndex].cargoOperationArray.find((co) =>
                                    isCargoTheSame(sourcePortCall.berthCallArray[bcIndex].cargoOperationArray[coIndex], co)
                                );
                                if (!targetCo) {
                                    coCont = false;
                                    coExist = false;
                                } else {
                                    const moveEventsCo = moveEventsCargoOperationFactory.createMoveEventsCargoOperationItem(
                                        sourcePortCall.berthCallArray[bcIndex].cargoOperationArray[coIndex].cargoOperationId,
                                        targetCo.cargoOperationId
                                    );
                                    moveEventsCoList.push(moveEventsCo);
                                    coIndex++;
                                    coCont = coIndex < sourcePortCall.berthCallArray[bcIndex].cargoOperationArray.length;
                                }
                            }
                            if (!coExist) {
                                bcTargetIndex++;
                                bcTargetCont = bcTargetIndex < bcTargetList.length;
                            } else {
                                bcTargetCont = false;
                                bcTargetExist = true;
                                moveEventsBc.toBerthCallId = bcTargetList[bcTargetIndex].berthCallId;
                                moveEventsBc.moveEventsCargoOperationList = moveEventsCoList;
                                if (moveEventsPortCall) {
                                    moveEventsPortCall.moveEventsBerthCallList.push(moveEventsBc);
                                }
                            }
                        } else {
                            bcTargetIndex++;
                            bcTargetCont = bcTargetIndex < bcTargetList.length;
                        }
                    }
                } else if (moveEventsPortCall) {
                    // No cargo ops source berth call
                    moveEventsPortCall.moveEventsBerthCallList.push(moveEventsBc);
                }
                if (!bcTargetExist) {
                    bcCont = false;
                    moveEventsPortCall = null;
                } else {
                    bcIndex++;
                    bcCont = bcIndex < sourcePortCall.berthCallArray.length;
                }
            } else {
                // No target berth call with same berthId
                bcCont = false;
                moveEventsPortCall = null;
            }
        }
        return moveEventsPortCall;
    };

    const findNewPortCall = (portCallList: PortCall[], moveFromPortCall: PortCall): MoveEventsPortCall | null => {
        let moveEventsPortCall: MoveEventsPortCall | null = null;
        if (portCallList && portCallList.length > 0) {
            let pcCont = true;
            let pcIndex = 0;
            while (pcCont) {
                moveEventsPortCall = berthCallsAndCargoOpsExist(moveFromPortCall, portCallList[pcIndex]);
                if (moveEventsPortCall) {
                    pcCont = false;
                } else {
                    pcIndex++;
                    pcCont = pcIndex < portCallList.length;
                }
            }
        }
        return moveEventsPortCall;
    };

    const moveToPortCallItem = (portCallArray: PortCall[], eventList: TankersEvent[], moveFromPortCall: PortCall): MoveEventsPortCall | null => {
        if (!moveFromPortCall.isDeleted) {
            return null;
        }

        // Find events for moveFromBerthCall
        const evList = eventList.filter((ev) => !ev.deleted && ev.portCallId === moveFromPortCall.portCallId);
        if (!evList || evList.length === 0) {
            return null;
        }
        const targetPcList = getPortCallsFromPortIdWithoutEvents(portCallArray, moveFromPortCall, eventList);
        const targetPcListSameVoyageNumber = targetPcList.filter((pc) =>
            hasSameVoyageNumber(moveFromPortCall.voyageNumberPresentationString, pc.voyageNumberPresentationString)
        );
        if (targetPcListSameVoyageNumber && targetPcListSameVoyageNumber.length > 0) {
            const moveFromPc = findNewPortCall(targetPcListSameVoyageNumber, moveFromPortCall);
            if (moveFromPc) {
                return moveFromPc;
            }
        }
        const targetPcListDifferentVoyageNumber = targetPcList.filter(
            (pc) => !hasSameVoyageNumber(moveFromPortCall.voyageNumberPresentationString, pc.voyageNumberPresentationString)
        );
        if (targetPcListDifferentVoyageNumber && targetPcListDifferentVoyageNumber.length > 0) {
            return findNewPortCall(targetPcListDifferentVoyageNumber, moveFromPortCall);
        }
        return null;
    };

    const getPort = (rotation: Rotation, portId: number | null): Port | null => {
        if (portId) {
            const pc = rotation.portCallArray.find((o) => o.port && o.port?.portId === portId);
            if (pc && pc.port) {
                return newPortFromCode(pc.port, null, 0);
            } else {
                const port = portInPortClusterList.find((o) => o.portId === portId);
                if (port) {
                    return newPortFromCode(port, null, 0);
                }
            }
        }
        return null;
    };

    // Check if an event's port call is part of a port cluster
    const isEventFromPortCluster = (ev: TankersEvent): boolean => {
        // Events from port calls that are part of a port cluster will have portClusterInitialPortId set
        return !!ev.portClusterInitialPortId;
    };

    // Handles port calls, berth calls and cargo operations
    // Take into account: port clusters
    const addRemovedRotationNodes = (rotation: Rotation, fullEventList: TankersEvent[]) => {
        if (!fullEventList || fullEventList.length === 0) {
            return;
        }
        const eventsLinkedToNodesMissingInRotation = findEventsWithoutRotationNode(rotation, fullEventList);

        eventsLinkedToNodesMissingInRotation.forEach((ev: TankersEvent) => {
            const portCallExistInRotation = getPortRotationNode(rotation, ev.portCallId, ev.eventTypeContextId);

            if (!portCallExistInRotation) {
                // Check if this port call was filtered by stowage integration based on voyage number
                // This happens when the port call's voyage is >= firstVoyageOrcaWebStowageImosSync
                // In this case, the port call is not in the rotation because it's managed by the stowage system
                // and we should not create a deleted placeholder for it
                const voyageNum1 = ev.voyageNumber1 ? +ev.voyageNumber1 : null;
                const voyageNum2 = ev.voyageNumber2 ? +ev.voyageNumber2 : null;
                const threshold = selectedVessel?.firstVoyageOrcaWebStowageImosSync;

                if (threshold && voyageNum1 && voyageNum2) {
                    // Port call is filtered by stowage integration if:
                    // - Both voyage numbers are below threshold, OR
                    // - voyage2 is at or above threshold
                    const isFilteredByStowage = (voyageNum1 < threshold && voyageNum2 < threshold) || voyageNum2 >= threshold;

                    if (isFilteredByStowage) {
                        // Port call was filtered by stowage integration, don't create deleted placeholder
                        return;
                    }
                }
                //const port: Port | null = getPort(rotation, ev.portId);
                ev.isEditable = false;
                if (ev.eventTypeContextId) {
                    // Create new port call
                    const newPortCall = newDeletedPortCallFromCode(
                        ev.portCallId,
                        getPort(rotation, ev.portId),
                        ev.voyageNumber ?? '',
                        ev.voyageNumber1 ? ev.voyageNumber1.toString() : '',
                        ev.voyageNumber2 ? ev.voyageNumber2.toString() : null,
                        portCash,
                        ev.eventTypeContextId === ReportLocationEnum.Sea ? RotationTreeEnum.Sea : RotationTreeEnum.PortCall,
                        ev.eventTypeContextId,
                        ev.portClusterInitialPortId,
                        ev.portClusterInitialPortName,
                        ev.portName
                    );
                    if (ev.berthCallId) {
                        const newBerthCallItem = createDeletedBerthCall(rotation, fullEventList, ev, newPortCall);
                        if (newBerthCallItem) {
                            newPortCall.berthCallArray.push(newBerthCallItem);
                        }
                    }
                    rotation.portCallArray.push(newPortCall);
                }
            } else {
                if (
                    !portCallExistInRotation.port ||
                    !portCallExistInRotation.port.timeZoneArray ||
                    portCallExistInRotation.port.timeZoneArray.length === 0
                ) {
                    ev.isEditable = false;
                }
                if (ev.berthCallId) {
                    const newBerthCallItem = createDeletedBerthCall(rotation, fullEventList, ev, portCallExistInRotation);
                    if (newBerthCallItem) {
                        portCallExistInRotation.berthCallArray.push(newBerthCallItem);
                    }
                }
            }
        });
    };

    const reorganizeDeletedPortCalls = (rotation: Rotation, fullEventList: TankersEvent[]) => {
        rotation.portCallArray.forEach((pc) => {
            if (pc.isDeleted) {
                // Move deleted port call to the correct position in the rotation
                const moveToPortCall = moveToPortCallItem(rotation.portCallArray, fullEventList, pc);
                if (moveToPortCall) {
                    const indFrom = rotation.portCallArray.findIndex((o) => o.portCallId === moveToPortCall.fromPortCallId);
                    if (indFrom > -1) {
                        rotation.portCallArray.splice(indFrom, 1);
                    }
                    const indTo = rotation.portCallArray.findIndex((o) => o.portCallId === moveToPortCall.toPortCallId);
                    if (indTo > -1) {
                        rotation.portCallArray.splice(indTo + 1, 0, pc);
                    }
                }
            }
        });
    };

    const getNextPortCall = (selectedPortCallId: number | null, rotation: Rotation): PortCall | null => {
        if (selectedPortCallId && rotation && rotation.portCallArray.length > 0) {
            const portCallIndex = rotation.portCallArray.findIndex((portCall) => !portCall.isDeleted && portCall.portCallId === selectedPortCallId);
            if (portCallIndex > -1 && portCallIndex < rotation.portCallArray.length - 1) {
                return rotation.portCallArray[portCallIndex + 2];
            }
        }
        return null;
    };

    const getImosPortFunctionFromNode = (node: IRotationNode): string | null => {
        switch (node.type) {
            case RotationTreeEnum.Sea:
                return node.imosPortFunction;
            case RotationTreeEnum.PortCall:
                return node.imosPortFunction;
            case RotationTreeEnum.BerthCall: {
                const bc = node as BerthCall;
                if (bc.imosPortFunction) return bc.imosPortFunction;
                const portCall = getParentPortCallFromNode(node);
                if (portCall) {
                    return portCall.imosPortFunction;
                }
                break;
            }
            case RotationTreeEnum.CargoOperation: {
                const co = node as CargoOperation;
                if (co.imosPortFunction) return co.imosPortFunction;
                const berthCall = getParentBerthCallFromNode(node);
                if (berthCall) {
                    if (berthCall.imosPortFunction) return berthCall.imosPortFunction;
                    const portCall2 = getParentPortCallFromNode(berthCall);
                    if (portCall2) {
                        return portCall2.imosPortFunction;
                    }
                }
                break;
            }
        }
        return null;
    };

    return {
        getPortCall,
        getBerthCall,
        getCargoOperation,
        getPreviousPortCall,
        getNextPortCall,
        setNextPortCall,
        getRequiredContextObj,
        filterCargoOperation,
        getPortCallFromNode,
        getBerthCallFromNode,
        getCargoOperationFromNode,
        getPortCallIdFromNode,
        getParentPortCallFromNode,
        getParentBerthCallFromNode,
        getMovedBerthCallRotationNode,
        getBerthUnlockedByUser,
        getParentBerthForMovedToCargoOpSamePortCall,
        getCargoOpRotationNode,
        addRemovedRotationNodes,
        reorganizeDeletedPortCalls,
        setBerthUnlockedByUser,
        getParentPortForMovedToBerthCall,
        getParentBerthForMovedToCargoOp,
        getBerthCallsForPortCall,
        getParentBerthForMovedFromCargoOp,
        moveToPortCallItem,
        resetMovedFromClickedFlag,
        getTradeName,
        getPortCallIndex,
        getBerthCallIndex,
        getCargoOperationIndex,
        getImosPortFunctionFromNode,
        rotationAndImosSyncNotUpToDateMsg,
        rotationAndImosSyncNotUpToDateTooltip,
        getCargoOperationFromId,
    };
};
